import { useCallback, useEffect, useState } from 'react'
import Cropper, { type Area } from 'react-easy-crop'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface AvatarCropDialogProps {
  open: boolean
  /** Object URL of the file the user picked. Cleared in cleanup. */
  imageSrc: string | null
  onCancel: () => void
  /** Called with the cropped square as a Blob (PNG). */
  onConfirm: (blob: Blob) => void
  pending?: boolean
}

/**
 * Phase 7 — square crop dialog for Avatars. Wraps `react-easy-crop` with a 1:1 aspect lock and
 * canvas → Blob output. Save sends the blob upstream; Cancel discards.
 *
 * <p>Output is a PNG blob — the backend re-encodes to WebP (or JPEG fallback) and resizes to the
 * canonical 256×256, so client-side format choice doesn't actually matter beyond "something the
 * server accepts."
 */
export const AvatarCropDialog = ({
  open,
  imageSrc,
  onCancel,
  onConfirm,
  pending,
}: AvatarCropDialogProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)

  // Reset when a new image lands so we don't carry over the previous file's framing.
  useEffect(() => {
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
  }, [imageSrc])

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels)
  }, [])

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return
    const blob = await renderCrop(imageSrc, croppedAreaPixels)
    if (blob) onConfirm(blob)
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Crop your Avatar</DialogTitle>
          <DialogDescription>
            Drag and zoom to frame. Square crop; the server resizes to 256×256.
          </DialogDescription>
        </DialogHeader>

        {imageSrc && (
          <div className="relative h-72 w-full overflow-hidden rounded-md bg-muted">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              cropShape="round"
              showGrid={false}
            />
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor="avatar-crop-zoom" className="text-xs font-medium text-muted-foreground">
            Zoom
          </label>
          <input
            id="avatar-crop-zoom"
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={pending || !croppedAreaPixels}>
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const renderCrop = async (imageSrc: string, area: Area): Promise<Blob | null> => {
  const image = await loadImage(imageSrc)
  const canvas = document.createElement('canvas')
  canvas.width = area.width
  canvas.height = area.height
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height)
  return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'))
}

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.addEventListener('load', () => resolve(img))
    img.addEventListener('error', (e) => reject(e))
    img.src = src
  })
